import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const L = (es, en) => ((navigator.language || 'es').startsWith('es') ? es : en);

const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-slate-50 flex flex-col items-center px-4 py-10">
    <p className="text-sm font-bold tracking-wide text-[#30B0B0] mb-6">CORPORATE TALENT HUB</p>
    <div className="w-full max-w-lg bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
      <h1 className="text-xl font-bold text-slate-800">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      {children}
    </div>
    <p className="text-[11px] text-slate-400 mt-6 max-w-lg text-center">
      {L('Tus documentos se almacenan de forma segura y solo son accesibles para el personal autorizado. No se publican enlaces públicos.',
         'Your documents are stored securely and are only accessible to authorized staff. No public links are exposed.')}
    </p>
  </div>
);

const Consent = ({ who, checked, onChange }) => (
  <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer" data-testid="intake-consent">
    <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span>
      {L(`Acepto el tratamiento de mis datos personales y documentos por ${who} con fines de reclutamiento y selección, conforme al aviso de privacidad (Ley 81/2019 Panamá / LFPDPPP México). Puedo retirar mi consentimiento solicitándolo al responsable.`,
         `I consent to the processing of my personal data and documents by ${who} for recruitment purposes, per the privacy notice (Panama Law 81/2019 / Mexico LFPDPPP). I may withdraw consent by contacting the controller.`)}
    </span>
  </label>
);

const FilePick = ({ onFile, disabled }) => (
  <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" disabled={disabled}
         onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }}
         className="block w-full text-sm" data-testid="intake-file-input" />
);

export function InviteIntake() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [consent, setConsent] = useState(false);
  const [docType, setDocType] = useState('cv');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/intake/invite/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e.response?.status === 410
        ? L('Este enlace ha caducado.', 'This link has expired.')
        : L('Enlace no válido o ya utilizado.', 'Invalid or already used link.')));
  }, [token]);

  const upload = async (f) => {
    if (!f || !consent) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f); fd.append('doc_type', docType); fd.append('consent', 'true');
      const r = await axios.post(`${API}/public/intake/invite/${token}/upload`, fd);
      setFiles((p) => [...p, r.data.filename]);
    } catch (e) { setErr(e.response?.data?.detail || 'error'); }
    finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try { await axios.post(`${API}/public/intake/invite/${token}/complete`); setDone(true); }
    catch { setErr('error'); } finally { setBusy(false); }
  };

  if (err && !info) return <Shell title={L('Enlace no disponible', 'Link unavailable')} subtitle={err} />;
  if (!info) return <Shell title="…" />;
  if (done) {
    return (
      <Shell title={L('¡Documentos entregados!', 'Documents submitted!')}
             subtitle={L('Gracias. El equipo de reclutamiento revisará tu información.', 'Thank you. The recruitment team will review your information.')}>
        <p className="text-sm" data-testid="intake-done">{files.length} {L('archivo(s) entregado(s).', 'file(s) submitted.')}</p>
      </Shell>
    );
  }
  return (
    <Shell title={L('Entrega segura de documentos', 'Secure document submission')}
           subtitle={`${info.company_name || ''} · ${info.position || ''} · ${L('vence', 'expires')} ${String(info.expires_at).slice(0, 10)}`}>
      <p className="text-sm text-slate-600">{L('Hola', 'Hello')} {info.candidate_name} — {L('puedes subir hasta', 'you can upload up to')} {info.max_files} {L('archivos (CV, títulos, certificaciones). No necesitas crear una cuenta.', 'files (CV, degrees, certifications). No account needed.')}</p>
      <Consent who={info.company_name || 'la empresa'} checked={consent} onChange={setConsent} />
      <select value={docType} onChange={(e) => setDocType(e.target.value)} className="h-9 w-full rounded-md border px-2 text-sm" data-testid="intake-doctype">
        <option value="cv">{L('CV / Hoja de vida', 'CV / Résumé')}</option>
        <option value="diploma">{L('Título académico', 'Academic degree')}</option>
        <option value="certification">{L('Certificación', 'Certification')}</option>
        <option value="other">{L('Otro', 'Other')}</option>
      </select>
      <FilePick onFile={upload} disabled={!consent || busy} />
      {files.map((f, i) => <p key={i} className="text-xs text-emerald-700" data-testid={`intake-uploaded-${i}`}>✓ {f}</p>)}
      {err && <p className="text-xs text-rose-600">{String(err)}</p>}
      <button onClick={complete} disabled={!files.length || busy}
              className="w-full h-10 rounded-md bg-[#1B2834] text-white text-sm font-semibold disabled:opacity-40"
              data-testid="intake-complete-btn">
        {L('Confirmar entrega', 'Confirm submission')}
      </button>
    </Shell>
  );
}

function PublicForm({ title, subtitle, who, submit, extra = null, testid }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [area, setArea] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);
  const onFile = async (f) => {
    if (!f || !consent || !name || !email) { setErr(L('Completa nombre, email y consentimiento antes de subir.', 'Fill name, email and consent before uploading.')); return; }
    setBusy(true); setErr(null);
    try { await submit({ f, name, email, area }); setDone(true); }
    catch (e) { setErr(e.response?.data?.detail || 'error'); }
    finally { setBusy(false); }
  };
  if (done) return <Shell title={L('¡CV recibido!', 'CV received!')} subtitle={L('Gracias por tu interés. Te contactaremos si tu perfil avanza.', 'Thank you for your interest. We will contact you if your profile advances.')}><p data-testid={`${testid}-done`} className="text-sm">✓</p></Shell>;
  return (
    <Shell title={title} subtitle={subtitle}>
      <input className="h-9 w-full rounded-md border px-2 text-sm" placeholder={L('Nombre completo', 'Full name')} value={name} onChange={(e) => setName(e.target.value)} data-testid={`${testid}-name`} />
      <input className="h-9 w-full rounded-md border px-2 text-sm" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`${testid}-email`} />
      {extra && extra({ area, setArea })}
      <Consent who={who} checked={consent} onChange={setConsent} />
      <FilePick onFile={onFile} disabled={busy} />
      {err && <p className="text-xs text-rose-600">{String(err)}</p>}
    </Shell>
  );
}

export function PublicVacancy() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/jobs/${slug}`).then((r) => setV(r.data))
      .catch(() => setErr(L('Vacante no disponible.', 'Vacancy not available.')));
  }, [slug]);
  if (err) return <Shell title={L('Vacante no disponible', 'Vacancy unavailable')} subtitle={err} />;
  if (!v) return <Shell title="…" />;
  return (
    <PublicForm testid="job-apply"
      title={`${v.position_title}`}
      subtitle={`${v.company_name}${v.department ? ' · ' + v.department : ''}${v.location ? ' · ' + v.location : ''}`}
      who={v.company_name}
      submit={async ({ f, name, email }) => {
        const fd = new FormData();
        fd.append('file', f); fd.append('name', name); fd.append('email', email); fd.append('consent', 'true');
        await axios.post(`${API}/public/jobs/${slug}/apply`, fd);
      }} />
  );
}

export function TalentPoolIntake() {
  return (
    <PublicForm testid="pool-apply"
      title={L('Únete al Talent Pool de CTH', 'Join the CTH Talent Pool')}
      subtitle={L('Estás entregando tu CV a Corporate Talent Hub (H&M Advisory), no a una empresa específica. Solo el equipo autorizado de CTH gestiona este banco de talento.',
                  'You are submitting your CV to Corporate Talent Hub (H&M Advisory), not to a specific company. Only authorized CTH staff manage this talent pool.')}
      who="Corporate Talent Hub (H&M Advisory)"
      extra={({ area, setArea }) => (
        <input className="h-9 w-full rounded-md border px-2 text-sm" placeholder={L('Área de interés (opcional)', 'Area of interest (optional)')}
               value={area} onChange={(e) => setArea(e.target.value)} data-testid="pool-apply-area" />
      )}
      submit={async ({ f, name, email, area }) => {
        const fd = new FormData();
        fd.append('file', f); fd.append('name', name); fd.append('email', email);
        fd.append('area', area || ''); fd.append('consent', 'true');
        await axios.post(`${API}/public/talent-pool/apply`, fd);
      }} />
  );
}
